var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, LoadingButton, Alert } from '@one-thd/sui-atomic-components';
import { ManageUsersTable } from '../manage-user-roles/user-roles-table';
import { USER_FORM_MODE, } from '../../models/b2b/add-manage-users';
import { addManageUsersApi } from '../../api/b2b/add-manage-user';
import { proAllowanceApi } from '../../api/b2b/pro-allowance';
import { getApiError } from '../../api/axios-util';
import { addUserValidationSchema } from './validation';
import { ERR_INVALID_EMAIL, ERR_SAVE, } from '../../constants/response-error-messages';
import { EMAIL_DUPLICATE } from '../../constants/field-error-messages';
import { useAddManageUsers } from '../../hooks/use-add-manage-users';
import { useModal } from '../../hooks/use-modal';
import { TAlertStatus, TAlertType } from '../../models/common/alert-messages';
import { setAddUserOverlayAnalytics, setEditUserOverlayAnalytics, setAddUserSuccessAnalytics, setEditUserSuccessAnalytics, setUserPostPageErrorAnalytics, } from '../../utils/add-manage-user-analytics';
import { scrollToTop } from '../../utils/general';
import { ConvertToAdminModal, } from '../modal/convert-to-admin/convert-to-admin';
import { UserFormFields } from './user-form-fields';
export var UserForm = function (_a) {
    var _b;
    var onSuccess = _a.onSuccess, onCancel = _a.onCancel, formMode = _a.formMode;
    var _c = useState(false), loading = _c[0], setLoading = _c[1];
    var _d = useAddManageUsers(), editableUser = _d.editableUser, isPartOfRunnerAllowance = _d.isPartOfRunnerAllowance, alertMessage = _d.alertMessage, setAlertMessage = _d.setAlertMessage;
    var _e = useState(false), submitDisabled = _e[0], setSubmitDisabled = _e[1];
    var _f = useModal(), openModal = _f.openModal, hideModal = _f.hideModal, setComponentProps = _f.setComponentProps;
    var isEditMode = formMode === USER_FORM_MODE.EDIT_USER;
    var setDefaultValues = function () { return ({
        role: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.role) || null,
        email: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.email) || '',
        firstName: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.firstName) || '',
        lastName: (editableUser === null || editableUser === void 0 ? void 0 : editableUser.lastName) || '',
    }); };
    var setUserAPIErrors = function (error) {
        var _a = getApiError(error, 'users'), errorCode = _a.errorCode, errorMessage = _a.errorMessage;
        if (errorCode === 'USER_ERR_253') {
            setError('email', EMAIL_DUPLICATE);
        }
        else if (errorCode === 'UREG_ERR_101') {
            setError('email', ERR_INVALID_EMAIL);
        }
        else if (errorCode === 'USER_ERR_275') {
            setError('email', { message: errorMessage });
        }
        else {
            setAlertMessage({
                alertStatus: TAlertStatus.ERROR,
                text: ERR_SAVE.message,
                type: TAlertType.GENERAL,
            });
        }
        setUserPostPageErrorAnalytics(isEditMode ? 'edit user' : 'add user', 'inline', errorCode, errorMessage);
    };
    var formMethods = useForm({
        resolver: yupResolver(addUserValidationSchema),
        defaultValues: isEditMode
            ? setDefaultValues()
            : { email: '', firstName: '', lastName: '', role: null },
        mode: 'onTouched',
        delayError: 250,
    });
    var setValue = formMethods.setValue, setError = formMethods.setError, formErrors = formMethods.formState.errors, watch = formMethods.watch;
    var selectedRole = watch('role');
    useEffect(function () {
        if (isEditMode) {
            setEditUserOverlayAnalytics();
            setSubmitDisabled(true);
            setValue('role', (editableUser === null || editableUser === void 0 ? void 0 : editableUser.role) || null);
        }
        else {
            setAddUserOverlayAnalytics();
        }
    }, [editableUser]);
    useEffect(function () {
        Object.values(formErrors).forEach(function (error) {
            var errorMsg = error.message;
            scrollToTop();
            setUserPostPageErrorAnalytics(isEditMode ? 'edit user' : 'add user', 'inline', 'n/a', errorMsg);
        });
    }, [formErrors]);
    var handleCardClick = function (role) {
        if (isEditMode)
            setSubmitDisabled(role === (editableUser === null || editableUser === void 0 ? void 0 : editableUser.role));
        setValue('role', role);
    };
    var addUser = function (_a) {
        var role = _a.role, email = _a.email, firstName = _a.firstName, lastName = _a.lastName;
        setLoading(true);
        addManageUsersApi
            .addUser({ role: role, email: email, firstName: firstName, lastName: lastName })
            .then(function (_a) {
            var users = _a.data.users;
            if (role)
                setAddUserSuccessAnalytics(role);
            setLoading(false);
            var newUser = users[0];
            if (newUser.userInviteExpirationDate) {
                onSuccess(newUser.userInviteExpirationDate);
            }
            else {
                onSuccess();
            }
        })
            .catch(function (error) {
            setLoading(false);
            setUserAPIErrors(error);
            scrollToTop();
        });
    };
    var fetchAndDisableAllowance = function (userId, formData) {
        setLoading(true);
        return proAllowanceApi
            .getActiveAllowance(userId)
            .then(function (_a) {
            var allowance = _a.data;
            if (!(allowance === null || allowance === void 0 ? void 0 : allowance.allowanceId)) {
                return editUser(formData);
            }
            setLoading(false);
            return new Promise(function (resolve) {
                openModal(ConvertToAdminModal, {
                    isLoading: false,
                    onConvertToAdmin: function () {
                        setComponentProps({ isLoading: true });
                        proAllowanceApi
                            .disableAllowance(allowance.allowanceId)
                            .then(function () {
                            return editUser(formData);
                        })
                            .catch(function (error) {
                            setUserAPIErrors(error);
                        })
                            .finally(function () {
                            resolve();
                            setComponentProps({ isLoading: false });
                            hideModal();
                        });
                    },
                    hideModal: function () {
                        hideModal();
                        resolve();
                        onCancel();
                    },
                });
            });
        })
            .catch(function (error) {
            setLoading(false);
            setUserAPIErrors(error);
            return Promise.resolve();
        });
    };
    var handleEditUser = function (_a) {
        var role = _a.role, firstName = _a.firstName, lastName = _a.lastName, email = _a.email;
        if (!editableUser) {
            return;
        }
        if (!isPartOfRunnerAllowance || role !== 'Admin') {
            editUser({
                role: role,
                firstName: firstName,
                lastName: lastName,
                email: email,
            });
            return;
        }
        fetchAndDisableAllowance(editableUser.userId, {
            role: role,
            firstName: firstName,
            lastName: lastName,
            email: email,
        });
    };
    var editUser = function (values) {
        var role = values.role;
        if (!role || !editableUser) {
            throw new Error('no role or user provided');
        }
        var payload = __assign(__assign({}, editableUser), { role: role });
        setLoading(true);
        return addManageUsersApi
            .updateUser(payload)
            .then(function () {
            if (role)
                setEditUserSuccessAnalytics(role);
            onSuccess();
        })
            .catch(function (error) {
            setUserAPIErrors(error);
            scrollToTop();
        })
            .finally(function () {
            setLoading(false);
        });
    };
    var onSubmitUserInfo = function (formData) {
        if (isEditMode) {
            handleEditUser(formData);
        }
        else {
            addUser(formData);
        }
    };
    var formButtonText = isEditMode ? 'Save Changes' : 'Add User';
    return (React.createElement(React.Fragment, null,
        React.createElement("h1", { className: "sui-text-2xl sui-font-bold sui-mb-6", "data-testid": "".concat(isEditMode ? 'edit' : 'add', "-user-title") },
            isEditMode ? 'Edit' : 'Add',
            " User"),
        React.createElement("div", { className: "sui-text-lg sui-my-8" },
            React.createElement("div", { className: "sui-mb-6" },
                React.createElement("span", { className: "sui-font-bold" }, "Step 1: "),
                isEditMode ? 'Edit' : 'Add',
                " User Details"),
            React.createElement(UserFormFields, { formMethods: formMethods, onSubmit: onSubmitUserInfo, isEditMode: isEditMode },
                alertMessage && (React.createElement("div", { className: "sui-my-4" },
                    React.createElement(Alert, { status: "error" }, alertMessage === null || alertMessage === void 0 ? void 0 : alertMessage.text))),
                React.createElement("div", { className: "sui-text-lg sui-mb-4" },
                    React.createElement("span", { className: "sui-font-bold" }, "Step 2:"),
                    " Select User Type"),
                React.createElement("div", { className: "sui-text-base sui-mb-6" }, "What type of account access will this user have?"),
                React.createElement("div", { className: "sui-w-[370px] sm:sui-w-[561px]" },
                    React.createElement(ManageUsersTable, { role: selectedRole, onClick: handleCardClick })),
                React.createElement("div", { className: "sui-flex sui-pt-4 sui-flex-col md:sui-flex-row" },
                    React.createElement("div", { className: "sui-order-2 sm:sui-order-1 sui-mb-4 md:sui-mr-6" },
                        React.createElement(Button, { type: "button", variant: "secondary", onClick: onCancel, "data-testid": "cancel-btn" }, "Cancel")),
                    React.createElement("div", { className: "sui-order-1 sm:sui-order-2 sui-mb-4 md:sui-w-1/2" },
                        React.createElement(LoadingButton, { type: "submit", loading: loading, disabled: submitDisabled, "data-testid": "".concat(isEditMode ? 'save-changes' : 'add-user', "-btn") }, formButtonText))))),
        !selectedRole && formErrors.role && (React.createElement(Alert, { status: "error" }, (_b = formErrors.role) === null || _b === void 0 ? void 0 : _b.message)),
        React.createElement("div", { id: "AssignUserRolesB2B" })));
};
